import React from 'react';
import { Box } from '@mui/material';

export interface AutoGridProps {
  /** Number of columns - can be responsive object */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /** Gap between items (CSS value) */
  gap?: string;
  /** Row gap (overrides gap for rows) */
  rowGap?: string;
  /** Column gap (overrides gap for columns) */
  columnGap?: string;
  /** Children */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
}

/**
 * AutoGrid - Simple CSS grid with row-first auto flow
 * 
 * Use for layouts where items should flow left-to-right, then wrap to next row.
 * Supports responsive column counts.
 * 
 * @example
 * ```tsx
 * <AutoGrid columns={{ xs: 1, sm: 2 }} gap="1rem">
 *   <span>Item 1</span>
 *   <span>Item 2</span>
 *   <span>Item 3</span>
 *   <span>Item 4</span>
 * </AutoGrid>
 * // Mobile: 1 column, Desktop: 2 columns
 * ```
 */
export function AutoGrid({
  columns = 2,
  gap,
  rowGap,
  columnGap,
  children,
  className,
}: AutoGridProps) {
  const getGridTemplateColumns = () => {
    if (typeof columns === 'number') {
      return `repeat(${columns}, auto)`;
    }
    // Return responsive object for MUI sx prop
    return {
      xs: columns.xs ? `repeat(${columns.xs}, auto)` : undefined,
      sm: columns.sm ? `repeat(${columns.sm}, auto)` : undefined,
      md: columns.md ? `repeat(${columns.md}, auto)` : undefined,
      lg: columns.lg ? `repeat(${columns.lg}, auto)` : undefined,
      xl: columns.xl ? `repeat(${columns.xl}, auto)` : undefined,
    };
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(),
        gridAutoFlow: 'row',
        gap,
        rowGap,
        columnGap,
        justifyContent: 'start',
      }}
    >
      {children}
    </Box>
  );
}

export default AutoGrid;
